import React from "react";
import { STORE_ID as DEFAULT_STORE_ID } from "../lib/chatApi";

export interface StoreOption {
  id: string;
  name: string;
}

// Known test stores for the SPA environment.
// Extend this list as needed when new stores are added.
const KNOWN_STORES: StoreOption[] = [
  { id: "roxhop-neFhog-4hapmi", name: "Roxhop" },
  { id: "xxxxx-xxxxx-xxxxx", name: "XXXXX demo" },
  { id: "f6429b4f-4daa-4a37-b18a-05ebc1395141", name: "OJ" },
];

// Ensure the current default store is always present in the options.
export const STORE_OPTIONS: StoreOption[] = [
  { id: DEFAULT_STORE_ID, name: "Default store" },
  ...KNOWN_STORES,
].filter(
  (opt, index, arr) => index === arr.findIndex((o) => o.id === opt.id)
);

const STORAGE_KEY = "ww/spa/storeId";

function getInitialStoreId(): string {
  if (typeof window === "undefined") return DEFAULT_STORE_ID;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && STORE_OPTIONS.some((s) => s.id === saved)) {
      return saved;
    }
  } catch {}
  return DEFAULT_STORE_ID;
}

type StoreContextValue = {
  storeId: string;
  setStoreId: (id: string) => void;
};

const StoreContext = React.createContext<StoreContextValue | undefined>(
  undefined
);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [storeId, setStoreIdState] = React.useState<string>(() =>
    getInitialStoreId()
  );

  const setStoreId = React.useCallback((id: string) => {
    setStoreIdState(id);
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {}
  }, []);

  const value = React.useMemo(
    () => ({ storeId, setStoreId }),
    [storeId, setStoreId]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreContextValue {
  const ctx = React.useContext(StoreContext);
  if (!ctx) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return ctx;
}


